// eyeq — quota policy + movement contracts.
//
// The client baseline (eyeq_quota, Σ=214,928) is an IMMUTABLE snapshot — it is
// never edited. All flexibility lives on top of it:
//   - eyeq_quota_policy: per-cell mode — 'fixed' (exactly this, no moves),
//     'range' (moves allowed within [floor, cap]), 'open' (no bounds).
//     No policy row = 'range' with floor 0 and no cap.
//   - eyeq_quota_moves: append-only ledger of reallocations (from-cell −qty,
//     to-cell +qty, who/why/when). effective = baseline + Σin − Σout.
// This keeps "what the client asked" and "what we decided to do" as separate,
// auditable facts, and any experiment is reversible by a compensating move.
import { z } from 'zod';
import { EyeqShipRowSchema } from './delivery';

export const EyeqQuotaModeSchema = z.enum(['fixed', 'range', 'open']);
export type EyeqQuotaMode = z.infer<typeof EyeqQuotaModeSchema>;

export const EyeqQuotaCellRefSchema = z.object({
  locale: z.string().min(1),
  category: z.string().min(1),
  sub_category: z.string().min(1),
});
export type EyeqQuotaCellRef = z.infer<typeof EyeqQuotaCellRefSchema>;

// ── POST /api/eyeq/quota/policy — upsert a cell's policy ─────────────────────
export const EyeqQuotaPolicySetReqSchema = EyeqQuotaCellRefSchema.extend({
  mode: EyeqQuotaModeSchema,
  floor: z.number().int().min(0).nullable().default(null),
  cap: z.number().int().min(0).nullable().default(null),
  actor: z.string().min(1),
});
export type EyeqQuotaPolicySetReq = z.infer<typeof EyeqQuotaPolicySetReqSchema>;

// ── POST /api/eyeq/quota/move — reallocate qty between two cells ─────────────
export const EyeqQuotaMoveReqSchema = z.object({
  from: EyeqQuotaCellRefSchema,
  to: EyeqQuotaCellRefSchema,
  qty: z.number().int().min(1),
  reason: z.string().min(1),
  actor: z.string().min(1),
});
export type EyeqQuotaMoveReq = z.infer<typeof EyeqQuotaMoveReqSchema>;

export const EyeqQuotaMoveResSchema = z.object({
  move_id: z.number().int(),
  from_effective: z.number().int(),
  to_effective: z.number().int(),
});
export type EyeqQuotaMoveRes = z.infer<typeof EyeqQuotaMoveResSchema>;

// ── GET /api/eyeq/quota/effective?locale= ────────────────────────────────────
export const EyeqQuotaEffectiveCellSchema = z.object({
  locale: z.string(),
  category: z.string(),
  sub_category: z.string(),
  baseline: z.number().int(),   // client snapshot — never changes
  delta: z.number().int(),      // Σ moves in − Σ moves out
  effective: z.number().int(),  // baseline + delta — the operational target
  collected: z.number().int(),
  mode: EyeqQuotaModeSchema,
  floor: z.number().int().nullable(),
  cap: z.number().int().nullable(),
});
export const EyeqQuotaEffectiveResSchema = z.object({
  locale: z.string(),
  cells: z.array(EyeqQuotaEffectiveCellSchema),
  baseline_total: z.number().int(),
  effective_total: z.number().int(),
});
export type EyeqQuotaEffectiveRes = z.infer<typeof EyeqQuotaEffectiveResSchema>;

// ── one-button shipment (POST /api/eyeq/delivery/ship) ──────────────────────
// Preflight v2 (ADR-0051): everything the last two shipments had to be audited
// for by hand. A flagged asset never blocks the build — it stays behind with
// its issue recorded, which is the whole point of the one-button model.
export const EyeqShipIssueSchema = z.enum([
  'no_meta', 'invalid_meta', 'no_blob', 'no_participant', 'url_missing',
  'not_qa_accepted',   // reached the shipment without a manual accept verdict
  'url_in_nourl_cell', // a URL on a cell the client did not ask a URL for
  'na_url',            // 'N/A' — the protocol's escape hatch, retired as a habit
  'details_style',     // annotator voice in disambiguation_details
  'no_device_exif',    // no camera make/model — an export, not an original
  'uuid_gap',          // the participant's UUID counter drifted below what shipped
  'no_pid_pool',       // the locale's client PID pool base was never set
]);

export const EyeqShipReqSchema = z.object({
  statuses: z.array(z.string().min(1)).min(1).default(['accepted']),
  dry_run: z.boolean().default(false),
  actor: z.string().min(1),
});
export type EyeqShipReq = z.infer<typeof EyeqShipReqSchema>;

export const EyeqShipResSchema = z.object({
  shipment_id: z.string().nullable(),
  shipped: z.number().int(),
  flagged: z.array(z.object({ asset_id: z.string(), issue: EyeqShipIssueSchema })),
  // Non-blocking preflight findings (e.g. has_text_suspicious): the build still
  // produced a shipment, but a human should look before it leaves.
  warnings: z.array(z.string()).default([]),
  rows: z.array(EyeqShipRowSchema),
});
export type EyeqShipRes = z.infer<typeof EyeqShipResSchema>;

// ── GET /api/eyeq/delivery/readiness — страны-сетка для «Обзора» ────────────
export const EyeqReadinessResSchema = z.object({
  locales: z.array(z.object({
    locale: z.string(),
    ready: z.number().int(),
    flagged: z.number().int(),
    shipped: z.number().int(),
    target: z.number().int(),
  })),
});
export type EyeqReadinessRes = z.infer<typeof EyeqReadinessResSchema>;
